import { type TelemetryType } from '@shared/types/telemetry/TelemetryType.ts'
import { isRenderer } from '@highlight/ipc'
import { AnalyticsRenderer } from '@renderer/utils/analytics.renderer.ts'
import { legacyEventName, legacyEventProperties } from '@highlight/analytics'

if (!isRenderer) {
  throw new Error('Cannot import AmplitudeRenderer in non-renderer processes')
}

const log = {
  info: (message: string) => console.info(`[AmplitudeRenderer] ${message}`),
}

/** Amplitude is disconnected; kept so the renderer bootstrap doesn't change. */
export const initializeAmplitude = () => {}

/**
 * The one door every renderer event goes through.
 *
 * Every `AmplitudeRenderer.trackEvent`, `window.ampltd.trackEvent` and
 * `logAmplitudeEvent` call arrives here and goes to PostHog only; Amplitude is
 * disconnected. Only the name is translated; the properties pass through
 * untouched.
 *
 * `setUserProperties` and `incrementUserProperty` are no-ops. PostHog has no
 * increment, and setting person properties means `identify()`, which on a
 * signed-out device marks that anonymous id identified and silently makes the
 * real sign-in merge illegal, per ../../../../../.cursor/rules/analytics.mdc.
 * Person properties need their own change, gated on there being a user.
 */
const trackEvent = (eventName: string, eventProps?: object) => {
  log.info(`trackEvent: ${eventName}, properties: ${JSON.stringify(eventProps ?? {})}`)

  AnalyticsRenderer.capture(legacyEventName(eventName), legacyEventProperties(eventProps))
}

const trackSettingUpdate = (setting: string, value: any, eventProperties?: Record<string, any>) => {
  trackEvent('Setting Update', {
    setting,
    value,
    ...(eventProperties ?? {}),
  })
}

export const AmplitudeRenderer: TelemetryType = {
  trackEvent,
  trackSettingUpdate,
  setUserProperties: () => {},
  incrementUserProperty: () => {},
  flushAmplitude: async () => {},
}

if (typeof window !== 'undefined') {
  window.ampltd = AmplitudeRenderer
}
